import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useRequestActivityStore } from '@/stores/requestActivity';

/**
 * Shared in-flight-request instrumentation for axios clients. Wiring this onto
 * a client makes every request it issues increment the global
 * `requestActivity` counter on dispatch and decrement it on settle (success OR
 * error) — driving `GlobalProgressBar.vue`.
 *
 * Counted exactly once per *logical* request:
 *
 * - We tag each config with `ACTIVITY_TRACKED` the first time the request
 *   interceptor sees it. If a refresh/retry interceptor re-issues the SAME
 *   config object, the tag is already present so we don't double-increment.
 * - The response interceptor's onFulfilled AND onRejected both call `done()`,
 *   but only when the config still carries an un-cleared tag, and we clear the
 *   tag as we decrement so a single config can never decrement twice.
 *
 * Pinia may not be active yet for requests fired during boot (e.g. the very
 * first sign-in-config lookup before the app mounts). `withStore` swallows that
 * case so instrumentation never throws into the request/response path.
 */

const ACTIVITY_TRACKED = Symbol('oh.requestActivity.tracked');

// Augment the per-request config with our internal tracking flag. It is
// non-enumerable in spirit (a symbol key) so it never serialises onto the wire.
interface TrackedConfig {
  [ACTIVITY_TRACKED]?: boolean;
}

/**
 * Run `fn` with the activity store, swallowing the "Pinia not active yet"
 * case. Never lets store plumbing throw into the request/response pipeline.
 * @param fn Callback receiving the resolved store.
 */
function withStore(fn: (store: ReturnType<typeof useRequestActivityStore>) => void): void {
  try {
    fn(useRequestActivityStore());
  } catch {
    // Pinia not installed yet (request fired during boot) — ignore.
  }
}

/**
 * Increment the in-flight counter for `config` unless it's already tracked.
 * Idempotent per config object, so retry/refresh re-issues don't double-count.
 * @param config The axios request config being dispatched.
 */
function markStart(config: InternalAxiosRequestConfig & TrackedConfig): void {
  if (config[ACTIVITY_TRACKED]) return;
  config[ACTIVITY_TRACKED] = true;
  withStore((store) => store.start());
}

/**
 * Decrement the in-flight counter for `config` exactly once. Clears the tag so
 * a later interceptor in the same chain can't decrement the same request again.
 * @param config The settled request's config (may be undefined on hard failures).
 */
function markDone(config: (InternalAxiosRequestConfig & TrackedConfig) | undefined): void {
  // No config (e.g. a synchronous adapter throw before config attaches) → still
  // decrement defensively so a counted start always has a matching done. The
  // store floors at 0, so an over-decrement can never wedge the bar.
  if (config && !config[ACTIVITY_TRACKED]) return;
  if (config) config[ACTIVITY_TRACKED] = false;
  withStore((store) => store.done());
}

/**
 * Attach the request/response activity interceptors to an axios instance.
 * Both the authenticated `httpClient` and the public `iamPublic` client call
 * this so coverage is total with one shared implementation.
 *
 * The interceptors are purely additive: they re-return the response and
 * re-reject the error untouched, so they compose with the existing
 * global-error and auth interceptors without reordering or swallowing them.
 * @param client The axios instance to instrument.
 */
export function attachActivityInterceptors(client: AxiosInstance): void {
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      markStart(config);
      return config;
    },
    (error: AxiosError) => {
      // Request setup failed before dispatch — there was no start() to undo
      // (the success branch above never ran), so just re-reject.
      throw error;
    },
  );

  client.interceptors.response.use(
    (response) => {
      markDone(response.config as InternalAxiosRequestConfig & TrackedConfig);
      return response;
    },
    (error: AxiosError) => {
      markDone(error.config as (InternalAxiosRequestConfig & TrackedConfig) | undefined);
      throw error;
    },
  );
}
